import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useRef } from "react";

type RateImageEditorFinalizer = () => Promise<void>;

interface RateImageEditorsContextValue {
  registerImageEditor: (id: string, finalizer: RateImageEditorFinalizer) => void;
  unregisterImageEditor: (id: string) => void;
}

const RateImageEditorsContext = createContext<RateImageEditorsContextValue | null>(null);

interface RateImageEditorsProviderProps {
  value: RateImageEditorsContextValue;
}

export const RateImageEditorsProvider: FC<PropsWithChildren<RateImageEditorsProviderProps>> = ({ value, children }) => (
  <RateImageEditorsContext.Provider value={value}>{children}</RateImageEditorsContext.Provider>
);

export const useRateImageEditorsRegistry = () => {
  const finalizersRef = useRef<Map<string, RateImageEditorFinalizer>>(new Map());

  const registerImageEditor = useCallback((id: string, finalizer: RateImageEditorFinalizer) => {
    finalizersRef.current.set(id, finalizer);
  }, []);

  const unregisterImageEditor = useCallback((id: string) => {
    finalizersRef.current.delete(id);
  }, []);

  const finalizePendingImageEditors = useCallback(async () => {
    await Promise.all(Array.from(finalizersRef.current.values(), (finalize) => finalize()));
  }, []);

  const finalizeImageEditor = useCallback(async (id: string) => {
    await finalizersRef.current.get(id)?.();
  }, []);

  const registryValue = useMemo<RateImageEditorsContextValue>(
    () => ({ registerImageEditor, unregisterImageEditor }),
    [registerImageEditor, unregisterImageEditor]
  );

  return { registryValue, finalizePendingImageEditors, finalizeImageEditor };
};

export const useRateImageEditors = (): RateImageEditorsContextValue | null => useContext(RateImageEditorsContext);
